/**
 * 프로필 카드 이미지(관리자 공개 버킷 / 사용자 비공개 버킷) 버전 비교 캐시.
 * path + imageVersion 이 같으면 IndexedDB·메모리 Blob URL을 재사용하고,
 * 버전이 다르면 스토리지에서 다시 받아 캐시를 갱신한다.
 */

import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import type { ProfileCardSetSource } from '../types/profileCard';
import { getMemberPhotoSignedUrl } from './memberPhotoSignedUrlCache';

const DB_NAME = 'ClassPlannerProfileCardImageCache';
const DB_VERSION = 1;
const STORE = 'images';
const ADMIN_BUCKET = 'profile-card-images';

type CacheRecord = {
  buffer: ArrayBuffer;
  version: string;
  mime: string;
};

type MemoryEntry = {
  blobUrl: string;
  version: string;
};

const memoryByKey = new Map<string, MemoryEntry>();
const inflightByKey = new Map<string, Promise<string | null>>();

export function profileCardImageCacheKey(
  source: ProfileCardSetSource,
  storagePath: string
): string {
  return `${source}:${storagePath.trim()}`;
}

export function profileCardImageCacheVersion(imageVersion?: number | null): string {
  const n = Number(imageVersion);
  return Number.isFinite(n) && n > 0 ? String(Math.trunc(n)) : '0';
}

function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

async function idbGet(key: string): Promise<CacheRecord | undefined> {
  const db = await idbOpen();
  try {
    return await new Promise<CacheRecord | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const r = tx.objectStore(STORE).get(key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve(r.result as CacheRecord | undefined);
    });
  } finally {
    db.close();
  }
}

async function idbPut(key: string, record: CacheRecord): Promise<void> {
  const db = await idbOpen();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const r = tx.objectStore(STORE).put(record, key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve();
    });
  } finally {
    db.close();
  }
}

async function idbDelete(key: string): Promise<void> {
  const db = await idbOpen();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const r = tx.objectStore(STORE).delete(key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve();
    });
  } finally {
    db.close();
  }
}

function remember(key: string, version: string, buffer: ArrayBuffer, mime: string): string {
  const prev = memoryByKey.get(key);
  if (prev && prev.version !== version) {
    try {
      URL.revokeObjectURL(prev.blobUrl);
    } catch {
      /* ignore */
    }
  }
  const blobUrl = URL.createObjectURL(new Blob([buffer], { type: mime || 'image/webp' }));
  memoryByKey.set(key, { blobUrl, version });
  return blobUrl;
}

async function resolveSourceUrl(
  source: ProfileCardSetSource,
  storagePath: string,
  imageVersion?: number | null
): Promise<string | null> {
  const path = storagePath.trim();
  if (!path) return null;
  if (source === 'admin') {
    const { data } = supabase.storage.from(ADMIN_BUCKET).getPublicUrl(path);
    const base = data.publicUrl;
    const v = profileCardImageCacheVersion(imageVersion);
    return v !== '0' ? `${base}?v=${v}` : base;
  }
  return getMemberPhotoSignedUrl(path, imageVersion ?? undefined);
}

async function fetchImageArrayBuffer(
  sourceUrl: string
): Promise<{ buffer: ArrayBuffer; mime: string } | null> {
  try {
    const res = await fetch(sourceUrl, {
      method: 'GET',
      cache: 'default',
      credentials: 'omit',
      mode: 'cors',
    });
    if (!res.ok) return null;
    const buffer = await res.arrayBuffer();
    if (buffer.byteLength === 0) return null;
    const mime = res.headers.get('content-type')?.split(';')[0]?.trim() || 'image/webp';
    return { buffer, mime };
  } catch {
    return null;
  }
}

/** 동기 peek — 메모리에 같은 버전이 있으면 즉시 반환 */
export function peekCachedProfileCardImageUrl(
  source: ProfileCardSetSource,
  storagePath?: string | null,
  imageVersion?: number | null
): string | null {
  const path = (storagePath ?? '').trim();
  if (!path) return null;
  const key = profileCardImageCacheKey(source, path);
  const version = profileCardImageCacheVersion(imageVersion);
  const mem = memoryByKey.get(key);
  if (mem && mem.version === version) return mem.blobUrl;
  return null;
}

/**
 * 버전 동일 → 캐시 Blob URL, 다름 → 스토리지 fetch 후 캐시 갱신.
 * 반환 Blob URL은 호출 측에서 revoke 하지 말 것.
 */
export async function getCachedProfileCardImageUrl(
  source: ProfileCardSetSource,
  storagePath?: string | null,
  imageVersion?: number | null
): Promise<string | null> {
  const path = (storagePath ?? '').trim();
  if (!path) return null;
  const key = profileCardImageCacheKey(source, path);
  const version = profileCardImageCacheVersion(imageVersion);

  const mem = memoryByKey.get(key);
  if (mem && mem.version === version) return mem.blobUrl;

  let inflight = inflightByKey.get(key);
  if (!inflight) {
    inflight = (async (): Promise<string | null> => {
      try {
        const cached = await idbGet(key);
        if (cached?.buffer?.byteLength && cached.version === version) {
          return remember(key, version, cached.buffer, cached.mime || 'image/webp');
        }
      } catch {
        /* IDB 미지원 */
      }

      const sourceUrl = await resolveSourceUrl(source, path, imageVersion);
      if (!sourceUrl) return null;
      const got = await fetchImageArrayBuffer(sourceUrl);
      if (!got) {
        try {
          const stale = await idbGet(key);
          if (stale?.buffer?.byteLength) {
            return remember(key, stale.version, stale.buffer, stale.mime || 'image/webp');
          }
        } catch {
          /* ignore */
        }
        return null;
      }

      try {
        await idbPut(key, { buffer: got.buffer, version, mime: got.mime });
      } catch {
        /* ignore */
      }
      return remember(key, version, got.buffer, got.mime);
    })().finally(() => {
      inflightByKey.delete(key);
    });
    inflightByKey.set(key, inflight);
  }
  return inflight;
}

/** 업로드/저장 직후 로컬 blob을 캐시에 넣어 재 fetch 생략 */
export async function putProfileCardImageCacheFromBlob(
  source: ProfileCardSetSource,
  storagePath: string,
  imageVersion: number,
  blob: Blob
): Promise<string | null> {
  const path = storagePath.trim();
  if (!path || blob.size <= 0) return null;
  const key = profileCardImageCacheKey(source, path);
  const version = profileCardImageCacheVersion(imageVersion);
  const mime = blob.type || 'image/webp';
  const buffer = await blob.arrayBuffer();
  try {
    await idbPut(key, { buffer, version, mime });
  } catch {
    /* ignore */
  }
  return remember(key, version, buffer, mime);
}

export async function invalidateProfileCardImageCache(
  source: ProfileCardSetSource,
  storagePath: string
): Promise<void> {
  const key = profileCardImageCacheKey(source, storagePath.trim());
  if (!key.endsWith(':') && key.length > source.length + 1) {
    const mem = memoryByKey.get(key);
    if (mem) {
      try {
        URL.revokeObjectURL(mem.blobUrl);
      } catch {
        /* ignore */
      }
      memoryByKey.delete(key);
    }
    inflightByKey.delete(key);
    try {
      await idbDelete(key);
    } catch {
      /* ignore */
    }
  }
}

/** 버전 동일 시 메모리/IDB 재사용. 없거나 다르면 백그라운드 fetch. */
export function preloadProfileCardImages(
  items: Array<{
    source: ProfileCardSetSource;
    storagePath?: string | null;
    imageVersion?: number | null;
  }>
): void {
  for (const item of items) {
    const path = (item.storagePath ?? '').trim();
    if (!path) continue;
    if (peekCachedProfileCardImageUrl(item.source, path, item.imageVersion)) continue;
    void getCachedProfileCardImageUrl(item.source, path, item.imageVersion);
  }
}

export function useProfileCardImageUrl(
  source: ProfileCardSetSource,
  storagePath?: string | null,
  imageVersion?: number | null
): string | null {
  const path = (storagePath ?? '').trim();
  const version = profileCardImageCacheVersion(imageVersion);
  const [url, setUrl] = useState<string | null>(() =>
    peekCachedProfileCardImageUrl(source, path, imageVersion)
  );

  useEffect(() => {
    if (!path) {
      setUrl(null);
      return;
    }
    const peeked = peekCachedProfileCardImageUrl(source, path, imageVersion);
    setUrl(peeked);
    let cancelled = false;
    void getCachedProfileCardImageUrl(source, path, imageVersion).then((next) => {
      if (!cancelled) setUrl(next);
    });
    return () => {
      cancelled = true;
    };
  }, [source, path, version, imageVersion]);

  return url;
}
